/**
 * Input/output contracts for the travelItem bulk callable.
 *
 * One callable handles all four travel kinds (flight / lodging /
 * transport / other) via a discriminated row schema. Single-doc CRUD
 * stays on the direct Firestore path (`travelItemService.ts`); the bulk
 * path needs server-side label resolution (location labels, group names,
 * audience emails, optional session link) plus uniform email validation
 * against active eventMembership.
 *
 * Time fields per kind are sent as ISO datetime strings. The dialog
 * converts flight departure/arrival values from airport-local time, while
 * lodging, transport, and other travel use the event timezone.
 */

import { z } from 'zod';
import {
  transportModeSchema,
  flightDirectionSchema,
  transportSpendLimitSchema,
} from '../schemas/travel-item';

/** Optional reminder. Importers require leadMinutes/body/trigger time when enabled. */
const reminderInputSchema = z
  .object({
    enabled: z.boolean(),
    leadMinutes: z.number().int().positive(),
    body: z.string(),
  })
  .nullable();

const baseRowFields = {
  /**
   * Single audience for the row — a named audience name or an email
   * address (server detects `@`). Empty cell is rejected: travel items
   * have no category-default fallback (unlike sessions). Multi-audience
   * targeting goes through the admin compose form, not the workbook.
   */
  audience: z.string().min(1),
  reminder: reminderInputSchema,
  /** Optional UI affordance — session title; resolves server-side to sessionId. */
  sessionLinkTitle: z.string().nullable(),
} as const;

export const bulkCreateTravelItemsFlightRowSchema = z
  .object({
    kind: z.literal('flight'),
    ...baseRowFields,
    direction: flightDirectionSchema.nullable(),
    airline: z.string().nullable(),
    flightNumber: z.string().nullable(),
    departureAirport: z.string().nullable(),
    departureTerminal: z.string().nullable(),
    /** ISO datetime, UTC (admin already converted from event-local). */
    departureAt: z.string().nullable(),
    arrivalAirport: z.string().nullable(),
    arrivalTerminal: z.string().nullable(),
    arrivalAt: z.string().nullable(),
    confirmationCode: z.string().nullable(),
    notes: z.string().nullable(),
  })
  .strict();

export const bulkCreateTravelItemsLodgingRowSchema = z
  .object({
    kind: z.literal('lodging'),
    ...baseRowFields,
    /** Resolves server-side against eventLocations.label. Maps to dropoffLocationId. */
    locationLabel: z.string().nullable(),
    checkInAt: z.string().nullable(),
    checkOutAt: z.string().nullable(),
    confirmationCode: z.string().nullable(),
    notes: z.string().nullable(),
  })
  .strict();

export const bulkCreateTravelItemsTransportRowSchema = z
  .object({
    kind: z.literal('transport'),
    ...baseRowFields,
    mode: transportModeSchema,
    pickupLocationLabel: z.string().nullable(),
    dropoffLocationLabel: z.string().nullable(),
    /** Null for open-ended perks (Uber code valid throughout event). */
    pickupAt: z.string().nullable(),
    /** Rideshare voucher / ride confirmation. */
    code: z.string().nullable(),
    /** Optional voucher claim/redemption link. */
    voucherUrl: z.string().url().nullable().optional(),
    /** Optional spend cap on a perk. */
    spendLimit: transportSpendLimitSchema.nullable(),
    /** Optional validity window for perks — ISO datetime strings (UTC). */
    validityFrom: z.string().nullable(),
    validityUntil: z.string().nullable(),
    notes: z.string().nullable(),
  })
  .strict();

export const bulkCreateTravelItemsOtherRowSchema = z
  .object({
    kind: z.literal('other'),
    ...baseRowFields,
    title: z.string().min(1),
    description: z.string().nullable(),
    time: z.string().nullable(),
    notes: z.string().nullable(),
  })
  .strict();

export const bulkCreateTravelItemsRowSchema = z
  .discriminatedUnion('kind', [
    bulkCreateTravelItemsFlightRowSchema,
    bulkCreateTravelItemsLodgingRowSchema,
    bulkCreateTravelItemsTransportRowSchema,
    bulkCreateTravelItemsOtherRowSchema,
  ])
  .superRefine((row, ctx) => {
    if (!row.reminder?.enabled) return;
    if (row.kind === 'flight' && !row.departureAt) {
      addMissingReminderTriggerIssue(ctx, 'departureAt');
    } else if (row.kind === 'lodging' && !row.checkInAt) {
      addMissingReminderTriggerIssue(ctx, 'checkInAt');
    } else if (row.kind === 'transport' && !row.pickupAt) {
      addMissingReminderTriggerIssue(ctx, 'pickupAt');
    } else if (row.kind === 'other' && !row.time) {
      addMissingReminderTriggerIssue(ctx, 'time');
    }
  });

function addMissingReminderTriggerIssue(
  ctx: z.RefinementCtx,
  field: 'departureAt' | 'checkInAt' | 'pickupAt' | 'time',
): void {
  ctx.addIssue({
    code: z.ZodIssueCode.custom,
    path: [field],
    message: `${field} is required when reminder.enabled is true`,
  });
}

export type BulkCreateTravelItemsRow = z.infer<
  typeof bulkCreateTravelItemsRowSchema
>;

export const bulkCreateTravelItemsInputSchema = z
  .object({
    eventId: z.string().min(1),
    rows: z.array(bulkCreateTravelItemsRowSchema).min(1).max(500),
  })
  .strict();

export type BulkCreateTravelItemsInput = z.infer<
  typeof bulkCreateTravelItemsInputSchema
>;

export type BulkCreateTravelItemsRowStatus =
  | 'created'
  | 'unknown_location' // a referenced locationLabel doesn't exist in this event
  | 'unknown_audience' // audience cell didn't resolve (name not found, or email not on roster)
  | 'unknown_session' // sessionLinkTitle set but no matching session
  | 'failed';

export interface BulkCreateTravelItemsRowResult {
  index: number;
  /** Short row label for the result table — flight number / hotel label / mode / title. */
  label: string;
  kind: 'flight' | 'lodging' | 'transport' | 'other';
  status: BulkCreateTravelItemsRowStatus;
  travelItemId?: string;
  /** Populated when status === 'unknown_audience'. */
  unresolvedNames?: string[];
  message?: string;
}

export interface BulkCreateTravelItemsResponse {
  success: true;
  results: BulkCreateTravelItemsRowResult[];
  summary: {
    created: number;
    unknownLocation: number;
    unknownAudience: number;
    unknownSession: number;
    failed: number;
  };
}

// ---------------------------------------------------------------------------
// bulkResolveTravelItems — Phase C of the resolve-and-propose architecture.
//
// Same input as bulkCreateTravelItems, no writes. Returns per-row + per-cell
// proposals so the dialog can show "this lodging row would write into the
// Ritz-Carlton location, target the Skiers audience, link to session
// 'Welcome Reception'" before any database mutation. Surfaces ADR-057
// identity matches when audience cell is an email so duplicate-profile
// risk is visible in the same way as Sessions.
// ---------------------------------------------------------------------------

export const bulkResolveTravelItemsInputSchema =
  bulkCreateTravelItemsInputSchema;

export type BulkResolveTravelItemsInput = z.infer<
  typeof bulkResolveTravelItemsInputSchema
>;

export type BulkResolveTravelItemsRowStatus =
  | 'new'
  | 'unknown_location'
  | 'unknown_audience'
  | 'unknown_session'
  | 'failed';

export interface BulkResolveTravelItemsProposal {
  /** Resolved pickup location, when set on the row's kind and matched. */
  pickupLocation?: { id: string; label: string };
  /** Resolved dropoff location, when set on the row's kind and matched. */
  dropoffLocation?: { id: string; label: string };
  /** Resolved audience target. */
  audience?:
    | { source: 'named'; audienceId: string; name: string }
    | {
        source: 'personal';
        audienceId: string;
        userId: string;
        displayName: string;
        photoURL: string | null;
        priorEventNames: string[];
        pending?: false;
      }
    | {
        source: 'personal';
        audienceId: string;
        contactId: string;
        displayName: string;
        photoURL: string | null;
        priorEventNames: string[];
        pending: true;
      };
  /** Resolved sessionLink, when sessionLinkTitle was provided and matched. */
  sessionLink?: { id: string; title: string };
}

export interface BulkResolveTravelItemsRowResult {
  index: number;
  label: string;
  kind: 'flight' | 'lodging' | 'transport' | 'other';
  status: BulkResolveTravelItemsRowStatus;
  proposal?: BulkResolveTravelItemsProposal;
  unresolvedNames?: string[];
  message?: string;
}

export interface BulkResolveTravelItemsResponse {
  success: true;
  results: BulkResolveTravelItemsRowResult[];
  summary: {
    new: number;
    unknownLocation: number;
    unknownAudience: number;
    unknownSession: number;
    failed: number;
  };
}
